import {BaSeButton} from "@base/react"
import {useEffect, useRef, useState} from "react"
import {useLocation, useNavigate} from "react-router-dom"
import {eventTag, getCurrentCNPJ} from "../../services/utils"
import Loading from "../../components/loading"
import ApiPlannings from "../../services/planning"
import ModalDefault from "../../components/modals/modal-default"
import {ReactComponent as BlueFolderIcon} from "../../assets/images/icons/blue-folder.svg"
import {ReactComponent as GraphBarIcon} from "../../assets/images/icons/graph-bar.svg"
import {ReactComponent as ChartLineIcon} from "../../assets/images/icons/chart-line.svg"
import {ReactComponent as CalculatorIcon} from "../../assets/images/icons/calculator.svg"
import {ReactComponent as DiscussLineIcon} from "../../assets/images/icons/discuss-line.svg"
import WidgetPills from "../../components/widget-pills"
import {useApp} from "../../hooks/use-app"
import {toast} from "react-toastify"
import ToastContent from "../../components/toastContent"
import ComparisonLabAccess from "../../components/comparison-lab/access/access"
import FampeAccess from "../../components/comparison-lab/access/fampe-access"
import HomeHero from "../../components/home-hero"
import ApiRedirect from "../../services/redirect"
import {normalizeRedirectHtml} from "../../utils/redirectHtml"
import "./index.scss"

const event = {
  name: "home",
  category: "home-planejamento",
  action: "click",
}

const REDIRECT_AD_DISMISS_STORAGE_KEY = "redirect-ads-dismissed-until"
const REDIRECT_AD_DISMISS_DURATION_IN_MS = 24 * 60 * 60 * 1000
const REDIRECT_CONTACT_URL = "https://app.octagora.com/s/SebraePlanFinanEmpr"
const REDIRECT_AD_TRANSITION_DELAY_IN_MS = 1500
const REDIRECT_AD_TRANSITION_DURATION_IN_MS = 700

const thingsToDoList = [
  {
    id: "planning",
    icon: BlueFolderIcon,
    text: "Criar e concluir seu primeiro planejamento financeiro",
  },
  {
    id: "scenarios",
    icon: GraphBarIcon,
    text: "Comparar cenários e tomar decisões com mais segurança",
  },
  {
    id: "debts",
    icon: ChartLineIcon,
    text: "Organizar dívidas e acompanhar seu planejamento em tempo real",
  },
  {
    id: "simulations",
    icon: CalculatorIcon,
    text: "Simular opções e encontrar as melhores linhas de crédito",
  },
  {
    id: "contents",
    icon: DiscussLineIcon,
    text: "Acessar conteúdos e dicas personalizadas para o seu negócio",
  },
]

const fadeInAnimationClass = "animate__animated animate__fadeIn"
const fadeInDelayStep = 200

const Home = ({ account }) => {
  const { companies } = useApp()
  const currentCnpj = getCurrentCNPJ(companies) || null
  const [loading, setLoading] = useState(true)
  const [showModalSharedPlan, setShowSharedPlan] = useState({
    status: false,
    titlePlan: null
  })
  const [loadedPlans, setLoadedPlans] = useState(false);
  const [heroState, setHeroState] = useState("no-plannings");
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [monitoringMonth, setMonitoringMonth] = useState(null)
  const [monitoringYear, setMonitoringYear] = useState(null)
  const [numberOfPlannings, setNumberOfPlannings] = useState(0)
  const [redirectAdsQueue, setRedirectAdsQueue] = useState([])
  const [redirectAdTransitionPhase, setRedirectAdTransitionPhase] = useState("idle")
  const redirectAdDelayTimeoutRef = useRef(null)
  const redirectAdTransitionTimeoutRef = useRef(null)
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const ApiRedirectService = ApiRedirect()
  const activeRedirectAd = redirectAdsQueue[0] || null
  const activeAdHtml = activeRedirectAd
    ? normalizeRedirectHtml(activeRedirectAd.html)
    : ""
  const hasPendingAd = Boolean(activeRedirectAd && activeAdHtml)
  const isAdTransitioning = hasPendingAd && redirectAdTransitionPhase === "animating"
  const hasActiveAd = hasPendingAd && redirectAdTransitionPhase === "visible"
  const shouldRenderAdSlot = hasActiveAd
  const getFadeInDelayStyle = (stepIndex) => ({
    animationDelay: `${stepIndex * fadeInDelayStep}ms`,
  })

  const sharedPlanAccepted = routerLocation?.state?.sharedPlanAccepted
  const sharedPlanTitle = routerLocation?.state?.sharedPlanTitle || ''
  const ownLinkError = routerLocation?.state?.ownLinkError
  const ownLinkMessage = routerLocation?.state?.ownLinkMessage

  const getDismissedRedirectAds = () => {
    if (typeof window === "undefined") {
      return {}
    }

    try {
      const rawValue = window.localStorage.getItem(REDIRECT_AD_DISMISS_STORAGE_KEY)
      return rawValue ? JSON.parse(rawValue) : {}
    } catch (error) {
      return {}
    }
  }

  const setDismissedRedirectAds = (dismissedAds) => {
    if (typeof window === "undefined") {
      return
    }

    window.localStorage.setItem(
      REDIRECT_AD_DISMISS_STORAGE_KEY,
      JSON.stringify(dismissedAds)
    )
  }

  const getActiveDismissedRedirectAds = () => {
    const dismissedAds = getDismissedRedirectAds()
    const now = Date.now()
    const activeDismissedAds = Object.entries(dismissedAds).reduce((accumulator, [slug, expiresAt]) => {
      if (Number(expiresAt) > now) {
        accumulator[slug] = Number(expiresAt)
      }

      return accumulator
    }, {})

    if (Object.keys(activeDismissedAds).length !== Object.keys(dismissedAds).length) {
      setDismissedRedirectAds(activeDismissedAds)
    }

    return activeDismissedAds
  }

  const dismissRedirectAdFor24Hours = (slug) => {
    if (!slug) {
      return
    }

    const dismissedAds = getActiveDismissedRedirectAds()

    dismissedAds[slug] = Date.now() + REDIRECT_AD_DISMISS_DURATION_IN_MS
    setDismissedRedirectAds(dismissedAds)
  }

  useEffect(() => {
    if (sharedPlanAccepted === true && loadedPlans) {
      setShowSharedPlan({
        status: true,
        titlePlan: sharedPlanTitle,
      })
      eventTag(event?.name, "atendimento", "submit", `Solicitação de acesso: ${sharedPlanTitle}`);

      navigate(routerLocation.pathname, { replace: true, state: {} })
    } else if (ownLinkError) {
      toast.error(
        <ToastContent
          description={ownLinkMessage}
          title="Solicitação inválida."
        />
      )
      eventTag(event?.name, "atendimento", "submit", `Solicitação inválida: ${sharedPlanTitle}`);
      navigate(routerLocation.pathname, { replace: true, state: {} })
    } else if (sharedPlanAccepted === false) {
      toast.error(
        <ToastContent
          description={`O planejador recusou seu pedido de acesso ao ${sharedPlanTitle}.`}
          title="Solicitação recusada."
        />
      )
      eventTag(event?.name, "atendimento", "submit", `Solicitação recusada: ${sharedPlanTitle}`);
      navigate(routerLocation.pathname, { replace: true, state: {} })
    }
  }, [sharedPlanAccepted, sharedPlanTitle, navigate, routerLocation.pathname, ownLinkError, ownLinkMessage, loadedPlans])

  const getPlannings = () => {
    setLoading(true)

    ApiPlannings()
      .currentStatus({
        cnpj: currentCnpj,
      })
      .then((response) => {
        const { data } = response
        const hasCurrentStatus = Boolean(data?.status)

        setNumberOfPlannings(hasCurrentStatus ? 1 : 0)
        setProgressPercentage(data?.progress_percentual || 0)
        setMonitoringMonth(data?.monitoring?.month || null)
        setMonitoringYear(data?.monitoring?.year || null)

        if (!data?.status) {
          setHeroState("no-plannings")
          return
        }

        if (data.status === "overdue") {
          setHeroState("monitoring-overdue")
          return
        }

        if (data.status === "pending") {
          setHeroState("monitoring-reminder")
          return
        }

        if (data.status === "finished") {
          setHeroState("completed-no-monitoring")
          return
        }

        if (data.status === "in_progress") {
          setHeroState("in-progress")
          return
        }

        setHeroState("no-plannings")
      })
      .catch((err) => {
        console.warn("ERROR => ", err)
      })
      .finally(() => {
        setLoading(false)
        setLoadedPlans(true)
      })
  }

  const verifyActiveRedirects = () => {
    if (!currentCnpj) {
      setRedirectAdsQueue([])
      return
    }

    ApiRedirectService
      .verifyRedirects({
        cnpj: currentCnpj,
      })
      .then((response) => {
        const dismissedAds = getActiveDismissedRedirectAds()
        const redirectEntries = Object.entries(response?.data || response || {})
          .filter(([slug]) => !dismissedAds[slug])
          .sort(([, current], [, next]) => {
            const currentPriority = Number(current?.prioridade ?? Number.MAX_SAFE_INTEGER)
            const nextPriority = Number(next?.prioridade ?? Number.MAX_SAFE_INTEGER)
            return currentPriority - nextPriority
          })

        const [firstRedirectSlug, firstRedirectData] = redirectEntries[0] || []

        if (firstRedirectSlug && firstRedirectData?.html) {
          setRedirectAdsQueue(
            redirectEntries
              .filter(([, item]) => item?.html)
              .map(([slug, item]) => ({
                slug,
                html: item.html,
              }))
          )
        } else {
          setRedirectAdsQueue([])
        }
      })
      .catch(() => {
        setRedirectAdsQueue([])
      })
  }

  const advanceRedirectQueue = () => {
    setRedirectAdsQueue((currentQueue) => currentQueue.slice(1))
  }

  useEffect(() => {
    if (redirectAdDelayTimeoutRef.current) {
      clearTimeout(redirectAdDelayTimeoutRef.current)
      redirectAdDelayTimeoutRef.current = null
    }

    if (redirectAdTransitionTimeoutRef.current) {
      clearTimeout(redirectAdTransitionTimeoutRef.current)
      redirectAdTransitionTimeoutRef.current = null
    }

    if (!hasPendingAd) {
      setRedirectAdTransitionPhase("idle")
      return
    }

    setRedirectAdTransitionPhase("waiting")

    redirectAdDelayTimeoutRef.current = setTimeout(() => {
      setRedirectAdTransitionPhase("animating")

      redirectAdTransitionTimeoutRef.current = setTimeout(() => {
        setRedirectAdTransitionPhase("visible")
      }, REDIRECT_AD_TRANSITION_DURATION_IN_MS)
    }, REDIRECT_AD_TRANSITION_DELAY_IN_MS)

    return () => {
      if (redirectAdDelayTimeoutRef.current) {
        clearTimeout(redirectAdDelayTimeoutRef.current)
        redirectAdDelayTimeoutRef.current = null
      }

      if (redirectAdTransitionTimeoutRef.current) {
        clearTimeout(redirectAdTransitionTimeoutRef.current)
        redirectAdTransitionTimeoutRef.current = null
      }
    }
  }, [hasPendingAd, activeRedirectAd?.slug])

  const buildRedirectActionPayload = () => {
    if (!activeRedirectAd || !currentCnpj) {
      return null
    }

    return {
      redirect_slug: activeRedirectAd.slug,
      cnpj: currentCnpj,
    }
  }

  const handlePrimaryRedirectAction = () => {
    const payload = buildRedirectActionPayload()

    if (!payload) {
      return
    }

    setLoading(true)

    ApiRedirectService
      .initContact(payload)
      .then(() => {
        toast.success(
          <ToastContent
            title="Sucesso!"
            description="Solicitação de contato enviada com sucesso."
          />
        )

        advanceRedirectQueue()
        window.location.href = REDIRECT_CONTACT_URL
      })
      .catch((error) => {
        toast.error(
          <ToastContent
            title="Falha!"
            description={error?.message || "Não foi possível solicitar o contato agora."}
          />
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleSnoozeRedirectAction = () => {
    const payload = buildRedirectActionPayload()

    if (!payload) {
      return
    }

    setLoading(true)

    ApiRedirectService
      .remindLater(payload)
      .then(() => {
        toast.success(
          <ToastContent
            title="Sucesso!"
            description="Lembrete adiado com sucesso."
          />
        )

        advanceRedirectQueue()
      })
      .catch((error) => {
        toast.error(
          <ToastContent
            title="Falha!"
            description={error?.message || "Não foi possível adiar este lembrete agora."}
          />
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleDismissRedirectAction = () => {
    const payload = buildRedirectActionPayload()

    if (!payload) {
      return
    }

    setLoading(true)

    ApiRedirectService
      .doNotRemind(payload)
      .then(() => {
        toast.success(
          <ToastContent
            title="Sucesso!"
            description="Preferência atualizada com sucesso."
          />
        )

        advanceRedirectQueue()
      })
      .catch((error) => {
        toast.error(
          <ToastContent
            title="Falha!"
            description={error?.message || "Não foi possível atualizar esta preferência agora."}
          />
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleAdSlotClick = (event) => {
    const actionElement = event.target.closest("[data-transbordo-action]")

    if (!actionElement) {
      return
    }

    event.preventDefault()

    const { transbordoAction } = actionElement.dataset

    if (transbordoAction === "close") {
      if (activeRedirectAd) {
        dismissRedirectAdFor24Hours(activeRedirectAd.slug)
        advanceRedirectQueue()
      }
      return
    }

    if (transbordoAction === "primary") {
      handlePrimaryRedirectAction()
      return
    }

    if (transbordoAction === "snooze") {
      handleSnoozeRedirectAction()
      return
    }

    if (transbordoAction === "dismiss") {
      handleDismissRedirectAction()
    }
  }

  useEffect(() => {
    getPlannings()
    // eslint-disable-next-line
  }, [getCurrentCNPJ(companies)])

  useEffect(() => {
    verifyActiveRedirects()
    // eslint-disable-next-line
  }, [currentCnpj])

  return loading ? (
    <Loading fullPage />
  ) : (
    <div className="home-page">
      <ModalDefault
        show={showModalSharedPlan?.status}
        callBackClose={() => setShowSharedPlan({
          status: false,
          titlePlan: null
        })}
        square
        title="Solicitação de acesso em análise"
        classCustom="dialog-alert"
      >
        <p className="dialog-alert__description">
          Você solicitou acesso ao <strong>{showModalSharedPlan?.titlePlan}</strong>.
          <br/>
          Aguarde a aprovação do planejador.
        </p>
        <div className="dialog-alert__buttons">
          <BaSeButton value="Fechar"
                      buttonType="button"
                      color="#2E5DE6"
                      size="medium"
                      width="auto"
                      onClick={() => setShowSharedPlan({
                        status: false,
                        titlePlan: null
                      })} />
        </div>
      </ModalDefault>

      <div className="container">
        <div
          className={
            "content " + (numberOfPlannings > 0 ? "has-plannings" : "")
          }
        >
          <div className="salutation">
            <h2>
              Olá, <span>{account?.name ? account?.name.split(' ')[0] : "Empresário"}!</span>
            </h2>
          </div>
          <div
            id="div-with-no-planning"
            className={[
              "content-left",
              hasPendingAd ? "content-left--has-pending-ad" : "",
              isAdTransitioning ? "content-left--ad-animating" : "",
              hasActiveAd ? "content-left--ad-visible" : "",
            ].filter(Boolean).join(" ")}
          >
            <div className="content-left__hero-panel">
              <HomeHero
                hasPlannings={numberOfPlannings > 0}
                heroState={heroState}
                progressPercentage={progressPercentage}
                monitoringMonth={monitoringMonth}
                monitoringYear={monitoringYear}
                event={event}
                hasActiveAd={hasActiveAd}
              />
            </div>
            <div className="content-left__ad-panel">
              {shouldRenderAdSlot ? (
                <div
                  className="content-left__ad-slot content-left__ad-slot--entering"
                  onClick={handleAdSlotClick}
                  dangerouslySetInnerHTML={{ __html: activeAdHtml }}
                />
              ) : null}
            </div>
          </div>
          <div className="content-middle">
            <div
              className="things-to-do animate__animated animate__fadeIn"
              style={{ animationDelay: "300ms" }}
            >
              <h3 className="title-section">O que você pode fazer na planejadora?</h3>
              <div className="things-to-do__list">
                {thingsToDoList.map((item, index) => (
                  <div
                    className={`${fadeInAnimationClass} things-to-do__item`}
                    key={item.id}
                    style={getFadeInDelayStyle(index)}
                  >
                    <div className={`things-to-do__icon things-to-do__icon--${item.id}`}>
                      <item.icon aria-hidden="true" focusable="false" />
                    </div>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
              <div className="things-to-do__link">
                e muito mais!
              </div>
            </div>

            <div
              className="simulations animate__animated animate__fadeIn"
              style={{ animationDelay: "600ms" }}
            >
              <h3 className="title-section">Simulações</h3>
              <p className="simulations__description">
                Acesse ferramentas para simular cenários e tomar melhores decisões.
              </p>
              <ComparisonLabAccess />
              <FampeAccess />
            </div>
          </div>
          <div>
            <WidgetPills />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Home
